// @section 1 Thread route — in-app email reader + AI-drafted reply (V14 spine)
// Replaces the bounce-to-Gmail dead link. Object-detail right-column pattern
// per UI-TEMPLATE-V14.md §6.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getGmailFull, draftReply, sendReply, ADAM_SUBJECT, type GmailFull } from '../api';

export default function Thread() {
  const { messageId = '' } = useParams();
  const nav = useNavigate();
  const [msg, setMsg] = useState<GmailFull | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const [draftState, setDraftState] = useState<'idle' | 'loading' | 'done' | 'unavailable'>('idle');
  const [draftNote, setDraftNote] = useState<string>('');
  const [sendState, setSendState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [sendErr, setSendErr] = useState('');

  useEffect(() => {
    getGmailFull(ADAM_SUBJECT, messageId)
      .then(setMsg)
      .catch((e) => setErr(String(e)));
  }, [messageId]);

  const replyTo = useMemo(() => extractEmail(msg?.from || ''), [msg]);
  const replySubject = useMemo(
    () => (msg?.subject?.toLowerCase().startsWith('re:') ? msg.subject : `Re: ${msg?.subject || ''}`),
    [msg],
  );

  async function generate() {
    if (!msg) return;
    setDraftState('loading');
    const r = await draftReply({
      messageId: msg.id,
      subject: msg.subject,
      from: msg.from,
      bodyText: msg.bodyText.slice(0, 8000),
    });
    if (r.available && r.draft) {
      setReply(r.draft);
      setDraftState('done');
    } else {
      setDraftState('unavailable');
      setDraftNote(r.reason || 'AI draft unavailable.');
    }
  }

  async function send() {
    if (!msg || !reply.trim()) return;
    setSendState('sending');
    try {
      await sendReply(ADAM_SUBJECT, {
        to: replyTo,
        subject: replySubject,
        textBody: reply,
        htmlBody: reply.replace(/\n/g, '<br>'),
      });
      setSendState('sent');
    } catch (e) {
      setSendState('error');
      setSendErr(String(e));
    }
  }

  return (
    <div className="md:-mx-10 md:-my-8">
      <section className="bg-ink-950 text-paper-100 min-h-screen font-sans">
        <div className="px-4 py-5 md:px-10 md:py-7 max-w-[1400px] mx-auto">

          {/* Row 1 — breadcrumb */}
          <div className="flex items-center justify-between text-xs mb-6">
            <div className="text-muted">
              <span className="text-paper-100 cursor-pointer hover:underline" onClick={() => nav('/')}>Atlas</span>
              <span className="opacity-40"> / </span>
              <span className="text-paper-100 cursor-pointer hover:underline" onClick={() => nav('/')}>My Day</span>
              <span className="opacity-40"> / </span>
              <span className="text-paper-100/80">Thread</span>
            </div>
            <button onClick={() => nav('/')} className="text-[11px] text-muted hover:text-paper-100">‹ Back to My Day</button>
          </div>

          {err && <div className="rounded-xl border-2 border-stall/40 bg-ink-900 p-5 text-stall text-sm ring-1 ring-stall/10">{err}</div>}
          {!msg && !err && <div className="text-muted text-sm">Loading thread…</div>}

          {msg && (
            <>
              {/* Row 2 — title + meta */}
              <div className="flex flex-col md:flex-row items-start gap-4 md:gap-6 mb-8">
                <div className="flex-shrink-0 md:max-w-[60%]">
                  <div className="text-[11px] uppercase tracking-[3px] text-muted mb-2">
                    {cleanName(msg.from)} · {fmtDate(msg.date)}
                  </div>
                  <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-paper-100 mb-2 leading-tight">{msg.subject || '(no subject)'}</h1>
                  <p className="text-sm text-muted">to {msg.to || ADAM_SUBJECT}</p>
                </div>
                <div className="grid grid-cols-2 gap-3 md:flex-1">
                  <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-4">
                    <div className="text-[10px] uppercase tracking-[2px] text-muted font-semibold">From</div>
                    <div className="text-lg font-semibold tabular-nums mt-1 text-paper-100 truncate">{cleanName(msg.from)}</div>
                    <div className="text-[11px] text-muted mt-0.5 truncate">{extractEmail(msg.from)}</div>
                  </div>
                  <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-4">
                    <div className="text-[10px] uppercase tracking-[2px] text-muted font-semibold">Age</div>
                    <div className="text-4xl font-bold tabular-nums mt-1 text-watch">{ageDays(msg.date)}d</div>
                    <div className="text-[11px] text-muted mt-0.5">unanswered</div>
                  </div>
                </div>
              </div>

              {/* Row 3 — body grid */}
              <div className="grid gap-6 grid-cols-1 md:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">

                {/* LEFT — message + reply composer */}
                <div className="space-y-4">
                  <div className="rounded-xl border border-ink-800 bg-gradient-to-br from-ink-900 to-ink-900/40 p-5">
                    <div className="text-[10px] uppercase tracking-[2px] text-muted font-bold mb-2">Message</div>
                    <pre className="whitespace-pre-wrap text-sm text-paper-100/90 leading-relaxed font-sans">{msg.bodyText.slice(0, 6000)}</pre>
                  </div>

                  <div className="rounded-xl border-2 border-accent-500/40 bg-gradient-to-br from-ink-900 to-ink-900/40 p-5 ring-1 ring-accent-500/10">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-[10px] uppercase tracking-[2px] text-accent-400 font-bold">Your reply</div>
                      <button
                        onClick={generate}
                        disabled={draftState === 'loading'}
                        className="bg-accent-500 hover:bg-accent-600 disabled:opacity-50 text-ink-950 text-xs px-3 py-1.5 rounded font-semibold"
                      >
                        {draftState === 'loading' ? 'Drafting…' : '✨ AI draft'}
                      </button>
                    </div>
                    {draftState === 'unavailable' && (
                      <div className="text-[11px] text-watch mb-2">{draftNote} — type your reply below in the meantime.</div>
                    )}
                    <textarea
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      rows={10}
                      placeholder="Write a reply, or hit ✨ AI draft to get a starting point…"
                      className="w-full bg-ink-950 border border-ink-800 rounded-lg p-3 text-sm text-paper-100 leading-relaxed focus:outline-none focus:border-accent-500/60"
                    />
                    <div className="flex flex-wrap gap-2 mt-3 items-center">
                      <button
                        onClick={send}
                        disabled={!reply.trim() || sendState === 'sending' || sendState === 'sent'}
                        className="bg-accent-500 hover:bg-accent-600 disabled:opacity-40 text-ink-950 text-xs px-3 py-1.5 rounded font-semibold"
                      >
                        {sendState === 'sent' ? '✓ Sent' : sendState === 'sending' ? 'Sending…' : 'Send via Bridge'}
                      </button>
                      {draftState === 'done' && (
                        <button onClick={generate} className="bg-ink-800 hover:bg-ink-700 text-paper-100 text-xs px-3 py-1.5 rounded">Regenerate</button>
                      )}
                      <button onClick={() => nav('/')} className="text-xs text-muted hover:text-paper-100 px-2 py-1.5">Back to My Day</button>
                      {sendState === 'error' && <span className="text-stall text-[11px]">{sendErr}</span>}
                      {sendState === 'sent' && <span className="text-ok text-[11px]">Reply sent to {replyTo}.</span>}
                    </div>
                  </div>
                </div>

                {/* RIGHT — context + capture */}
                <div>
                  <div className="flex items-baseline justify-between mb-4">
                    <h2 className="text-2xl font-bold text-paper-100">🧭 Context</h2>
                  </div>
                  <div className="rounded-xl border border-ink-800 bg-ink-900/40 p-4 space-y-3 text-sm">
                    <Ctx k="Sender" v={cleanName(msg.from)} />
                    <Ctx k="Email" v={extractEmail(msg.from)} />
                    <Ctx k="Subject" v={msg.subject || '(none)'} />
                    <Ctx k="Received" v={fmtDate(msg.date)} />
                    <Ctx k="Reply to" v={replyTo} />
                    <Ctx k="From account" v={ADAM_SUBJECT} />
                  </div>

                  <div className="mt-4 rounded-xl bg-gradient-to-br from-accent-500 to-accent-600 text-ink-950 p-5">
                    <div className="text-[11px] uppercase tracking-[2px] opacity-70 font-semibold mb-2">Capture</div>
                    <button className="w-full bg-white text-accent-600 rounded-md py-2.5 font-semibold flex items-center justify-center gap-2">
                      <span>🎙</span><span>Voice + text + screenshot</span><kbd className="text-[10px] opacity-60">⌘⇧Space</kbd>
                    </button>
                  </div>
                </div>

              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}

function Ctx({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[2px] text-muted font-semibold">{k}</div>
      <div className="text-paper-100 break-words">{v}</div>
    </div>
  );
}
function cleanName(from: string): string {
  const m = from.match(/^"?([^"<]+?)"?\s*</);
  if (m) return m[1].trim();
  return extractEmail(from) || from;
}
function extractEmail(s: string): string {
  const m = s.match(/<?([^<>@\s]+@[^<>\s]+)>?/);
  return m ? m[1] : s;
}
function fmtDate(d?: string): string {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(+dt) ? d : dt.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
function ageDays(d?: string): number {
  if (!d) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 86_400_000));
}
